import React from "react";
import { Box, Paper, Stack, Typography, alpha } from "@mui/material";

/**
 * Header común para todos los dashboards - Diseño mejorado
 */
export default function DashboardHeader({ title, subtitle, icon, color = "primary" }) {
  const colorGradients = {
    primary: "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)",
    secondary: "linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)",
    success: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
    warning: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
    error: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
    info: "linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)",
  };

  const gradient = colorGradients[color] || colorGradients.primary;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 3, md: 4 },
        borderRadius: 5,
        overflow: "hidden",
        position: "relative",
        border: "1px solid",
        borderColor: "divider",
        background: (theme) =>
          `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
      }}
    >
      {/* Animated background decorations */}
      <Box
        sx={{
          position: "absolute",
          top: -100,
          right: -100,
          width: 300,
          height: 300,
          background: gradient,
          opacity: 0.08,
          borderRadius: "50%",
          filter: "blur(60px)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -80,
          left: "30%",
          width: 200,
          height: 200,
          background: "linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)",
          opacity: 0.05,
          borderRadius: "50%",
          filter: "blur(50px)",
        }}
      />

      {/* Decorative pattern */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.03,
          backgroundImage: `radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)`,
          backgroundSize: "24px 24px",
          pointerEvents: "none",
        }}
      />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={3}
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{ position: "relative" }}
      >
        {icon && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 64,
              height: 64,
              borderRadius: 4,
              background: gradient,
              color: "white",
              boxShadow: (theme) => `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`,
              "& svg": {
                fontSize: 32,
              },
            }}
          >
            {icon}
          </Box>
        )}
        <Stack spacing={0.5} sx={{ flex: 1 }}>
          <Typography
            variant="h4"
            fontWeight={800}
            sx={{
              background: gradient,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ maxWidth: 600 }}
            >
              {subtitle}
            </Typography>
          )}
        </Stack>

        {/* Decorative accent line */}
        <Box
          sx={{
            display: { xs: "none", md: "block" },
            width: 4,
            height: 60,
            borderRadius: 2,
            background: gradient,
            opacity: 0.6,
          }}
        />
      </Stack>
    </Paper>
  );
}
